import React, { useState, useEffect } from "react";
import { Menu, Dropdown } from "antd";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import {
  DownOutlined,
  RightOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

import "./Header.less";
import { getData } from "./apiUtils";
import MegaMenu from "./MegaMenu";
import {
  CATEGORIES,
  groupByCategory,
  isUpcoming,
  isExternal,
} from "./workshopUtils";

const { SubMenu } = Menu;

// The CMS submenu that the workshop columns get folded into. Both spellings
// are accepted so renaming it in Strapi cannot detach the mega menu, and the
// label is fixed here because the tab no longer renders any CMS page — it is
// built entirely from the workshop collection.
const EVENT_MENU_TITLES = ["event", "events"];
const EVENT_TAB_LABEL = "Events";

// The calendar is promoted out of the Event submenu into a tab of its own, so
// it is one click away from every page. It is a hardcoded app route (see
// App.js), not a CMS generic-page, hence the literal path.
const CALENDAR_URL = "/event-calendar";
const CALENDAR_TITLE = "Calendar";
const CALENDAR_KEY = "event-calendar";

// The horizontal menu now carries nine tabs. Below this width they no longer
// fit next to the logo, so the hamburger takes over well before the phone
// breakpoint that `isMobile` reports.
const MENU_COLLAPSE_WIDTH = 1180;

export default function Header({ isMobile }) {
  const [isNarrow, setIsNarrow] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth <= MENU_COLLAPSE_WIDTH
  );
  const [isCollapsedMenuOpen, setIsCollapsedMenuOpen] = useState(undefined);
  const [isEventMenuOpen, setIsEventMenuOpen] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${MENU_COLLAPSE_WIDTH}px)`);
    const onChange = (event) => setIsNarrow(event.matches);

    setIsNarrow(query.matches);
    // Safari < 14 only has the deprecated add/removeListener pair.
    if (query.addEventListener) query.addEventListener("change", onChange);
    else query.addListener(onChange);

    return () => {
      if (query.removeEventListener)
        query.removeEventListener("change", onChange);
      else query.removeListener(onChange);
    };
  }, []);

  const isCollapsedMenu = isMobile || isNarrow;

  const { isLoading, error, data } = useQuery(
    "header",
    getData({ apiRoute: "/header" })
  );
  const workshop_menu = useQuery(
    "workshop_menu",
    getData({ apiRoute: "/workshop-menu" })
  );
  if (isLoading) return null;
  if (error) {
    console.log(error);
    return null;
  }

  const workshopItems =
    (workshop_menu.data &&
      workshop_menu.data.menu &&
      workshop_menu.data.menu.workshop_pages) ||
    [];

  const groups = groupByCategory(workshopItems);

  const closeEventMenu = () => setIsEventMenuOpen(false);

  // Mobile keeps ordinary nested submenus — a four-column panel cannot work
  // inside the stacked inline menu.
  const renderCollapsedEventMenu = (menuItem) => (
    <SubMenu title={EVENT_TAB_LABEL} key={menuItem.id}>
      {CATEGORIES.map(({ key, label }) => {
        const group = groups[key];
        if (!group || !group.all.length) return null;

        return (
          <SubMenu
            key={`cat-${key}`}
            title={
              <span>
                {label}
                {group.hasUpcoming && (
                  <span className="mega-menu-badge">NEW</span>
                )}
              </span>
            }
          >
            {group.visible.map((item) => (
              <Menu.Item key={toKey(item.url)}>
                <MenuLink to={item.url}>
                  {item.title}
                  {isUpcoming(item) && (
                    <span className="mega-menu-badge">NEW</span>
                  )}
                </MenuLink>
              </Menu.Item>
            ))}
            {group.hasArchive && (
              <Menu.Item key={`archive-${key}`}>
                <Link to={`/workshop-archive?category=${key}`}>
                  Past editions archive <RightOutlined />
                </Link>
              </Menu.Item>
            )}
          </SubMenu>
        );
      })}
    </SubMenu>
  );

  const renderEventMegaMenu = (menuItem) => (
    <Menu.Item
      key={menuItem.id}
      className={`header-mega-trigger${
        isEventMenuOpen ? " header-mega-trigger-open" : ""
      }`}
    >
      <Dropdown
        visible={isEventMenuOpen}
        onVisibleChange={setIsEventMenuOpen}
        trigger={["hover"]}
        placement="bottomCenter"
        overlayClassName="mega-menu-overlay"
        overlay={
          <MegaMenu
            groups={groups}
            onNavigate={closeEventMenu}
          />
        }
      >
        <span className="header-mega-trigger-label">
          {EVENT_TAB_LABEL} <DownOutlined />
        </span>
      </Dropdown>
    </Menu.Item>
  );

  const renderCalendarTab = () => (
    <Menu.Item key={CALENDAR_KEY} className="header-calendar-tab">
      <Link to={CALENDAR_URL}>
        <CalendarOutlined /> {CALENDAR_TITLE}
      </Link>
    </Menu.Item>
  );

  // The calendar has a tab of its own now, so it is stripped from whichever
  // CMS submenu still lists it rather than appearing in both places while
  // Strapi is being tidied up.
  const withoutCalendar = (pages = []) =>
    pages.filter((page) => page.url !== CALENDAR_URL);

  const menuNodes = [];

  data.menu.forEach((menuItem) => {
    if (menuItem.__component !== "general.submenu") {
      if (menuItem.page.url === CALENDAR_URL) return;
      menuNodes.push(
        <Menu.Item key={toKey(menuItem.page.url)}>
          <Link to={menuItem.page.url}>{menuItem.page.title}</Link>
        </Menu.Item>
      );
      return;
    }

    // The Events tab absorbs the workshop listings that used to sit in their
    // own top-level menu, and the calendar sits immediately beside it.
    if (EVENT_MENU_TITLES.includes((menuItem.title || "").trim().toLowerCase())) {
      menuNodes.push(
        isCollapsedMenu
          ? renderCollapsedEventMenu(menuItem)
          : renderEventMegaMenu(menuItem)
      );
      menuNodes.push(renderCalendarTab());
      return;
    }

    const pages = withoutCalendar(menuItem.pages);
    if (!pages.length) return;

    menuNodes.push(
      <SubMenu title={menuItem.title} key={menuItem.id}>
        {pages.map((item) => (
          <Menu.Item key={toKey(item.url)}>
            <Link to={item.url}>{item.title}</Link>
          </Menu.Item>
        ))}
      </SubMenu>
    );
  });

  // Should the CMS ever stop returning an Event submenu, the calendar tab
  // still ships rather than disappearing with it.
  if (!menuNodes.some((node) => node.key === CALENDAR_KEY)) {
    menuNodes.push(renderCalendarTab());
  }

  return (
    <header className="header-wrapper">
      <div className="header">
        <Link to="/" className="header-logo">
          <img src={data.logo.url} alt="Aditya-L1 Support Cell Logo" />
        </Link>
        {isCollapsedMenu && (
          <div
            className={`header-hamburger-btn${
              isCollapsedMenuOpen ? " collapsed-menu-open" : ""
            }`}
            onClick={() => {
              setIsCollapsedMenuOpen(!isCollapsedMenuOpen);
            }}
          >
            <em />
            <em />
            <em />
          </div>
        )}
        <Menu
          mode={isCollapsedMenu ? "inline" : "horizontal"}
          theme="dark"
          inlineIndent={32}
          // Without this antd measures the available width and folds whatever
          // does not fit (Committees, in practice) behind an "..." button.
          // Paired with the tighter tab padding in Header.less so the full set
          // fits on a normal desktop.
          disabledOverflow={!isCollapsedMenu}
          className={`header-menu${isCollapsedMenu ? " collapsed-menu" : ""}${
            isCollapsedMenuOpen ? " collapsed-menu-open" : ""
          }`}
        >
          {menuNodes}
        </Menu>
      </div>
    </header>
  );
}

function MenuLink({ to, children }) {
  return isExternal(to) ? (
    <a href={to} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ) : (
    <Link to={to}>{children}</Link>
  );
}

const toKey = (path) => path.replace("/", "");
